/* eslint-disable @typescript-eslint/naming-convention */
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { switchMap, catchError, filter, take } from 'rxjs/operators';
import { from, Observable, BehaviorSubject, Subject, throwError } from 'rxjs';
import { Storage } from '@ionic/storage-angular';
import { AuthService } from '../services/http/auth/auth.service';
import { Router } from '@angular/router';
import { AlertService } from '../services/alert/alert.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private storage: Storage,
    private authService: AuthService,
    private router: Router,
    private alerts: AlertService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // No interceptar la ruta de refresh para evitar loop infinito
    if (request.url.includes('/auth/refresh')) {
      return next.handle(request);
    }

    return from(this.storage.get('token')).pipe(
      switchMap((token: string | null) => {
        const cloned = request.clone({
          setHeaders: {
            Accept: 'application/json, text/plain',
            'Content-Type': 'application/json;charset=UTF-8',
            Authorization: `Bearer ${token ?? ''}`
          }
        });
        return next.handle(cloned).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return this.handle401Error(cloned, next);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.storage.get('_token')).pipe(
        switchMap((rto: string | null) => {
          if (!rto) {
            this.isRefreshing = false;
            this.router.navigate(['login']);
            return throwError(() => new Error('No refresh token available'));
          }

          return this.authService.refreshToken(rto).pipe(
            switchMap((res) => {
              const { TOKEN, RTO, USER, TOKEN_exp, RTO_exp } = res;
              const tokenExpiresAt = new Date(TOKEN_exp).getTime();
              const rtoExpiresAt = new Date(RTO_exp).getTime();

              return from(Promise.all([
                this.storage.set('token', TOKEN),
                this.storage.set('_token', RTO),
                this.storage.set('user', USER),
                this.storage.set('token_expires_at', tokenExpiresAt),
                this.storage.set('rto_expires_at', rtoExpiresAt),
              ])).pipe(
                switchMap(() => {
                  this.isRefreshing = false;
                  this.refreshTokenSubject.next(TOKEN);

                  // Reintentar la request original con el nuevo token
                  const clonedWithNewToken = request.clone({
                    setHeaders: {
                      Accept: 'application/json, text/plain',
                      'Content-Type': 'application/json;charset=UTF-8',
                      Authorization: `Bearer ${TOKEN}`
                    }
                  });
                  return next.handle(clonedWithNewToken);
                })
              );
            }),
            catchError((error) => {
              this.isRefreshing = false;
              this.storage.clear();
              // Mostrar alert personalizado cuando el RTO expira
              this.alerts.setAlert({
                type: 'error',
                header: 'Sesión Expirada',
                message: 'Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.'
              });
              this.router.navigate(['login']);
              return throwError(() => error);
            })
          );
        })
      );
    } else {
      // Si ya está refrescando, esperar el nuevo token
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          const clonedWithNewToken = request.clone({
            setHeaders: {
              Accept: 'application/json, text/plain',
              'Content-Type': 'application/json;charset=UTF-8',
              Authorization: `Bearer ${token}`
            }
          });
          return next.handle(clonedWithNewToken);
        })
      );
    }
  }
}
